import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonWithdrawalInitialState = {
  withdrawals: [],
  withdrawalsTotal: 0,
  withdrawalsPages: 1,
  withdrawalDetail: null,
  withdrawalsLoading: false,
  withdrawalsError: null,
  withdrawalMutationLoading: false,
  withdrawalMutationError: null,
};

function url(path, params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, { method = "GET", params, body } = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(url(path, params), {
    method,
    headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "omit",
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch { throw new Error(`Withdrawals API returned a non-JSON response (${response.status}).`); }
  }
  if (!response.ok || data.error || data.success === false) throw new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
  return data?.data && typeof data.data === "object" ? data.data : data;
}

function pageOf(data) {
  const items = Array.isArray(data) ? data : data?.items || data?.withdrawals || data?.entries || [];
  return { items, total: data?.total ?? data?.totalItems ?? items.length, pages: data?.totalPages ?? data?.pages ?? 1 };
}

export function createResourcePersonWithdrawalActions(set) {
  return {
    clearWithdrawalDetail: () => set({ withdrawalDetail: null }),
    fetchWithdrawals: async (params = {}) => {
      set({ withdrawalsLoading: true, withdrawalsError: null });
      try {
        const result = pageOf(await request("/api/resource-person/withdrawals", { params }));
        set({ ...result, withdrawals: result.items, withdrawalsLoading: false });
        return { success: true, ...result };
      } catch (error) {
        set({ withdrawals: [], withdrawalsLoading: false, withdrawalsError: error.message });
        return { success: false, error: error.message };
      }
    },
    fetchWithdrawalById: async (id) => {
      try {
        const withdrawalDetail = await request(`/api/resource-person/withdrawals/${id}`);
        set({ withdrawalDetail });
        return { success: true, withdrawal: withdrawalDetail };
      } catch (error) {
        set({ withdrawalDetail: null, withdrawalsError: error.message });
        return { success: false, error: error.message };
      }
    },
    createWithdrawal: async (amount) => {
      set({ withdrawalMutationLoading: true, withdrawalMutationError: null });
      try {
        const withdrawal = await request("/api/resource-person/withdrawals", { method: "POST", body: { amount: Number(amount) } });
        set({ withdrawalMutationLoading: false, withdrawalDetail: withdrawal });
        return { success: true, withdrawal };
      } catch (error) {
        set({ withdrawalMutationLoading: false, withdrawalMutationError: error.message });
        return { success: false, error: error.message };
      }
    },
    cancelWithdrawal: async (id) => {
      set({ withdrawalMutationLoading: true, withdrawalMutationError: null });
      try {
        const withdrawal = await request(`/api/resource-person/withdrawals/${id}/cancel`, { method: "DELETE" });
        set({ withdrawalMutationLoading: false, withdrawalDetail: withdrawal });
        return { success: true, withdrawal };
      } catch (error) {
        set({ withdrawalMutationLoading: false, withdrawalMutationError: error.message });
        return { success: false, error: error.message };
      }
    },
  };
}
